import { DAY_OF_WEEK_VALUES, type DayOfWeek } from "@/api/model/common";

const MINUTES_PER_DAY = 24 * 60;
const DAY_SEQUENCE: DayOfWeek[] = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const DAY_INDEX: Record<DayOfWeek, number> = DAY_SEQUENCE.reduce(
  (acc, day, index) => {
    acc[day] = index;
    return acc;
  },
  {} as Record<DayOfWeek, number>
);

type DayOffset = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const clampMinutes = (value: number): number => {
  if (Number.isNaN(value) || value < 0) {
    return 0;
  }
  return value;
};

const parseTimeToMinutes = (time: string): number => {
  if (!time) return 0;
  const parts = time.split(":").map((part) => Number.parseInt(part, 10));
  if (parts.some((value) => Number.isNaN(value))) {
    return 0;
  }

  const [hours = 0, minutes = 0, seconds = 0] = parts;
  const totalMinutes = hours * 60 + minutes + Math.floor(seconds / 60);
  const normalized = totalMinutes % MINUTES_PER_DAY;
  return normalized < 0 ? normalized + MINUTES_PER_DAY : normalized;
};

const addDays = (day: DayOfWeek, offset: number): DayOfWeek => {
  const baseIndex = DAY_INDEX[day];
  const nextIndex = (baseIndex + offset) % DAY_SEQUENCE.length;
  return DAY_SEQUENCE[nextIndex as DayOffset];
};

const uniqueDays = (days: DayOfWeek[]): DayOfWeek[] => {
  const set = new Set<DayOfWeek>();
  for (const day of days) {
    if (DAY_INDEX[day] !== undefined) {
      set.add(day);
    }
  }
  return Array.from(set);
};

export interface PlanScheduleLike {
  id: string;
  name: string;
  daysOfWeek: DayOfWeek[];
  startTime: string;
  totalDurationMinutes: number;
}

export interface PlanStartCandidate {
  id?: string;
  name?: string;
  daysOfWeek: DayOfWeek[];
  startTime: string;
}

export interface PlanEndCandidate extends PlanStartCandidate {
  totalDurationMinutes: number;
}

export interface PlanSideInfo {
  id?: string;
  name?: string;
  startMinutes: number;
  endMinutes?: number;
}

export interface PlanSideInfoWithId extends PlanSideInfo {
  id: string;
  name: string;
  endMinutes: number;
}

export interface PlanOverlapConflict {
  type: "start" | "end";
  day: DayOfWeek;
  gapMinutes: number;
  minimumGapMinutes: number;
  candidate: PlanSideInfo;
  other: PlanSideInfoWithId;
}

export interface PlanOverlapValidationResult {
  isValid: boolean;
  conflict?: PlanOverlapConflict;
}

interface PlanOccurrence {
  plan: PlanScheduleLike;
  day: DayOfWeek;
  startMinutes: number;
  endMinutes: number;
}

const buildPlanOccurrences = (plan: PlanScheduleLike): PlanOccurrence[] => {
  const planDays = uniqueDays(plan.daysOfWeek);
  if (planDays.length === 0) {
    return [];
  }

  const startMinutes = parseTimeToMinutes(plan.startTime);
  const totalDuration = clampMinutes(plan.totalDurationMinutes);
  const occurrences: PlanOccurrence[] = [];

  for (const day of planDays) {
    if (totalDuration === 0) {
      occurrences.push({
        plan,
        day,
        startMinutes,
        endMinutes: startMinutes,
      });
      continue;
    }

    let remaining = totalDuration;
    let currentDay = day;
    let segmentStart = startMinutes;
    let dayOffset = 0;

    while (remaining > 0) {
      const available = Math.max(0, MINUTES_PER_DAY - segmentStart);
      const segmentDuration = Math.min(remaining, available);
      const segmentEnd = segmentStart + segmentDuration;

      occurrences.push({
        plan,
        day: currentDay,
        startMinutes: segmentStart,
        endMinutes: segmentEnd,
      });

      remaining -= segmentDuration;

      if (remaining <= 0) {
        break;
      }

      dayOffset += 1;
      currentDay = addDays(day, dayOffset);
      segmentStart = 0;
    }
  }

  return occurrences;
};

const toPlanSideInfoWithId = (occurrence: PlanOccurrence): PlanSideInfoWithId => ({
  id: occurrence.plan.id,
  name: occurrence.plan.name,
  startMinutes: occurrence.startMinutes,
  endMinutes: occurrence.endMinutes,
});

const toCandidateSideInfo = (
  candidate: PlanStartCandidate | PlanEndCandidate,
  startMinutes: number,
  endMinutes?: number
): PlanSideInfo => ({
  id: candidate.id,
  name: candidate.name,
  startMinutes,
  endMinutes,
});

const compareConflicts = (current: PlanOverlapConflict | undefined, next: PlanOverlapConflict): PlanOverlapConflict => {
  if (!current) return next;
  return next.gapMinutes < current.gapMinutes ? next : current;
};

export const validatePlanStartAgainstPlans = (
  candidate: PlanStartCandidate,
  plans: PlanScheduleLike[],
  minimumGapMinutes: number,
  options?: { ignorePlanIds?: string[] }
): PlanOverlapValidationResult => {
  const minGap = Math.max(0, minimumGapMinutes);
  const ignoreSet = new Set(options?.ignorePlanIds ?? []);
  const candidateDays = uniqueDays(candidate.daysOfWeek);

  if (candidateDays.length === 0) {
    return { isValid: true };
  }

  const candidateStartMinutes = parseTimeToMinutes(candidate.startTime);
  const occurrences = plans.filter((plan) => !ignoreSet.has(plan.id)).flatMap((plan) => buildPlanOccurrences(plan));

  let conflict: PlanOverlapConflict | undefined;

  for (const day of candidateDays) {
    for (const occurrence of occurrences) {
      if (occurrence.day !== day) continue;
      if (occurrence.startMinutes > candidateStartMinutes) continue;

      const gap = candidateStartMinutes - occurrence.endMinutes;
      if (gap < minGap) {
        const nextConflict: PlanOverlapConflict = {
          type: "start",
          day,
          gapMinutes: gap,
          minimumGapMinutes: minGap,
          candidate: toCandidateSideInfo(candidate, candidateStartMinutes),
          other: toPlanSideInfoWithId(occurrence),
        };
        conflict = compareConflicts(conflict, nextConflict);
      }
    }
  }

  return conflict ? { isValid: false, conflict } : { isValid: true };
};

export const validatePlanEndAgainstPlans = (
  candidate: PlanEndCandidate,
  plans: PlanScheduleLike[],
  minimumGapMinutes: number,
  options?: { ignorePlanIds?: string[] }
): PlanOverlapValidationResult => {
  const minGap = Math.max(0, minimumGapMinutes);
  const ignoreSet = new Set(options?.ignorePlanIds ?? []);

  if (!candidate.daysOfWeek || candidate.daysOfWeek.length === 0) {
    return { isValid: true };
  }

  const candidateSchedule: PlanScheduleLike = {
    id: candidate.id ?? "__candidate__",
    name: candidate.name ?? "",
    daysOfWeek: candidate.daysOfWeek,
    startTime: candidate.startTime,
    totalDurationMinutes: candidate.totalDurationMinutes,
  };

  const candidateOccurrences = buildPlanOccurrences(candidateSchedule);
  const otherOccurrences = plans
    .filter((plan) => !ignoreSet.has(plan.id))
    .flatMap((plan) => buildPlanOccurrences(plan));

  let conflict: PlanOverlapConflict | undefined;

  for (const occurrence of candidateOccurrences) {
    const candidateStart = occurrence.startMinutes;
    const candidateEnd = occurrence.endMinutes;

    for (const other of otherOccurrences) {
      if (other.day !== occurrence.day) continue;
      if (other.startMinutes < candidateStart) continue;

      const gap = other.startMinutes - candidateEnd;
      if (gap < minGap) {
        const nextConflict: PlanOverlapConflict = {
          type: "end",
          day: occurrence.day,
          gapMinutes: gap,
          minimumGapMinutes: minGap,
          candidate: toCandidateSideInfo(candidate, candidateStart, candidateEnd),
          other: toPlanSideInfoWithId(other),
        };
        conflict = compareConflicts(conflict, nextConflict);
      }
    }
  }

  return conflict ? { isValid: false, conflict } : { isValid: true };
};

export const minutesToTimeString = (minutes: number): string => {
  const normalized = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalized / 60)
    .toString()
    .padStart(2, "0");
  const mins = (normalized % 60).toString().padStart(2, "0");
  return `${hours}:${mins}`;
};

export const ensureValidPlanSchedule = (plan: PlanScheduleLike): PlanScheduleLike => ({
  ...plan,
  daysOfWeek: plan.daysOfWeek.filter((day): day is DayOfWeek =>
    (DAY_OF_WEEK_VALUES as readonly DayOfWeek[]).includes(day)
  ),
  totalDurationMinutes: clampMinutes(plan.totalDurationMinutes),
});

/**
 * Conflict pair returned by findAllPlanConflicts.
 * Used when checking all plans of the same project for mutual overlap.
 */
export interface PlanConflictPair {
  planA: PlanScheduleLike;
  planB: PlanScheduleLike;
  day: DayOfWeek;
  gapMinutes: number;
  minimumGapMinutes: number;
}

/**
 * Validate all plans within the same project against each other.
 * Returns every pair of plans whose occurrences on the same day are closer than minimumGapMinutes.
 *
 * Intended use:
 * - AdvanceCodecConfigModal: before enabling schedule resumption, ensure all plans respect the larger gap.
 */
export const findAllPlanConflicts = (
  plans: PlanScheduleLike[],
  minimumGapMinutes: number
): PlanConflictPair[] => {
  const minGap = Math.max(0, minimumGapMinutes);
  if (plans.length <= 1) return [];

  // Build all occurrences for all plans
  const allOccurrences = plans.flatMap((plan) => buildPlanOccurrences(plan));
  const conflicts: PlanConflictPair[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < allOccurrences.length; i++) {
    const a = allOccurrences[i];

    for (let j = i + 1; j < allOccurrences.length; j++) {
      const b = allOccurrences[j];
      if (a.day !== b.day) continue;
      if (a.plan.id === b.plan.id) continue; // same plan

      // Zero-duration plans (no steps yet) are intentionally included:
      // their endMinutes === startMinutes, so they must still respect the gap.

      // Normalize so "earlier" is first
      const [first, second] =
        a.startMinutes <= b.startMinutes ? [a, b] : [b, a];

      // Always compute the gap; negative = actual overlap
      const gap = second.startMinutes - first.endMinutes;
      if (gap < minGap) {
        const key = `${first.plan.id}-${second.plan.id}-${first.day}-${gap}`;
        if (!seen.has(key)) {
          seen.add(key);
          conflicts.push({
            planA: first.plan,
            planB: second.plan,
            day: first.day,
            gapMinutes: gap,
            minimumGapMinutes: minGap,
          });
        }
      }
    }
  }

  return conflicts;
};

export interface ResumptionHintOptions {
  /** Whether schedule resumption is currently enabled for the project's LIC. */
  resumptionEnabled: boolean;
  /** The minimum gap (minutes) applied when resumption is disabled. */
  withoutResumptionGap: number;
}

/**
 * Build the optional "disable resumption" hint shown alongside a plan-gap conflict.
 *
 * The hint is only useful when resumption is enabled AND the actual gap between the
 * two plans already satisfies the smaller without-resumption gap — in that case
 * disabling resumption would make the plan valid. If the gap is below that threshold
 * (e.g. a true overlap), disabling resumption would not help, so no hint is returned.
 *
 * See docs/adr/0001-minimum-plan-gap-by-schedule-resumption.md
 */
export const getResumptionHint = (
  conflict: PlanOverlapConflict,
  { resumptionEnabled, withoutResumptionGap }: ResumptionHintOptions
): string | null => {
  if (!resumptionEnabled) return null;
  if (conflict.gapMinutes < withoutResumptionGap) return null;

  return `Dica: com a "Retomada do Agendamento" desativada, o intervalo mínimo cai para ${withoutResumptionGap} minutos, o que permitiria criar este plano.`;
};
